/**
 * Pausa los efectos hover mientras la página se desplaza.
 *
 * Bug real reportado ("si hago scroll los elementos se tambalean"): con la
 * rueda del mouse el cursor queda quieto y el contenido pasa por debajo; cada
 * tarjeta o fila que cruzaba bajo el cursor disparaba su hover (sube 2-4px,
 * cambia la sombra) y volvía a bajar al salir. Medido en "Gestión de salas"
 * (84 cambios) y "Novedades" (336 cambios en ~4s de scroll).
 *
 * Durante el scroll se agrega `is-scrolling` a <html> (ver styles.css, que
 * desactiva los eventos de puntero del contenido) y se quita 150ms después
 * del último evento: al detenerse, solo el elemento que de verdad quedó bajo
 * el cursor muestra su hover. Solo escucha el scroll de la página (no el de
 * paneles internos), así que nunca interrumpe el scroll de un modal o lista.
 *
 * Se registra fuera de la zona de Angular (las versiones nativas que guarda
 * zone.js) para no disparar detección de cambios en cada evento de scroll.
 * Llamar una vez al iniciar la app: initScrollHoverGuard();
 */
const SCROLL_IDLE_MS = 150;

export function initScrollHoverGuard(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const root = document.documentElement;
  const nativeWindow = window as any;
  const addListener: typeof window.addEventListener = nativeWindow.__zone_symbol__addEventListener ?? window.addEventListener;
  const setTimer: typeof window.setTimeout = nativeWindow.__zone_symbol__setTimeout ?? window.setTimeout;
  const clearTimer: typeof window.clearTimeout = nativeWindow.__zone_symbol__clearTimeout ?? window.clearTimeout;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const onScroll = (event: Event) => {
    // En el panel el que se desplaza es <body> (html tiene overflow:hidden),
    // y su evento no llega a window: se escucha en captura y se acepta solo
    // el desplazamiento principal de la página, nunca el de un modal o lista.
    const target = event.target;
    if (target !== document && target !== root && target !== document.body) return;
    if (!root.classList.contains('is-scrolling')) root.classList.add('is-scrolling');
    if (timer !== undefined) clearTimer.call(window, timer);
    timer = setTimer.call(window, () => root.classList.remove('is-scrolling'), SCROLL_IDLE_MS);
  };
  addListener.call(document, 'scroll', onScroll, { passive: true, capture: true });
}
